import { useLayoutEffect, useRef, type ComponentProps, type KeyboardEvent, type Ref } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/** Browsers that size a textarea to its text from CSS (field-sizing); the rest are sized here from scrollHeight. */
const fieldSizing = typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content');

type GrowingTextareaProps = ComponentProps<typeof Textarea> & {
  /**
   * For a one-line value (a task title): the text wraps as the box grows, but Enter adds no line and a
   * pasted line break becomes a space.
   */
  singleLine?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
};

/**
 * SF-T-265: a textarea that starts as tall as an Input and grows with its text, up to about ten lines,
 * then scrolls. For an answer or note people may write a lot in (the AI builder's questions,
 * suggestions, disputes, proposed changes), where a one-line Input scrolls sideways on a phone.
 */
export function GrowingTextarea({ className, singleLine = false, onChange, onKeyDown, value, ref, ...props }: GrowingTextareaProps) {
  const own = useRef<HTMLTextAreaElement | null>(null);
  // Without field-sizing (older Safari), the height follows the text by hand.
  useLayoutEffect(() => {
    const el = own.current;
    if (fieldSizing || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);
  return (
    <Textarea
      {...props}
      ref={(el) => {
        own.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      rows={1}
      value={value}
      className={cn('min-h-9 max-h-[calc(10lh+1rem)] resize-none overflow-y-auto py-1.5', className)}
      onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (singleLine && e.key === 'Enter') e.preventDefault();
        onKeyDown?.(e);
      }}
      onChange={(e) => {
        if (singleLine && /[\r\n]/.test(e.target.value)) e.target.value = e.target.value.replace(/\s*[\r\n]+\s*/g, ' ');
        onChange?.(e);
      }}
    />
  );
}
